/**
 * Loom band patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const loomBandPatternsPack: ToilePack = {
    slug: 'loom-band-patterns',
    category: 'craft',
    name: 'Loom band patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'fishtail', label: 'Fishtail', subject: 'a loom band diagram for a fishtail bracelet on two pegs, bands in alternating blue and white drawn as figure-eight loops, a small finished bracelet shown below'},
        {key: 'starburst', label: 'Starburst', subject: 'a loom band pattern for a starburst bracelet on a three-column loom, the bands from each centre peg drawn out like the spokes of a star, numbered in placing order'},
        {key: 'ladder', label: 'Ladder', subject: 'a loom band diagram for a single-chain ladder bracelet in rainbow colours, the bands drawn as straight links up the loom with each colour in its place'},
    ],
    styles: [
        {key: 'colour', label: 'Colour chart', style: 'a clean loom diagram, the pegs small gray circles in even rows and each band a thick flat coloured line between two pegs with a small black number at its start', palette: 'bright rubber band colours on a white ground', framing: 'the diagram centred on a plain white sheet with an even margin'},
        {key: 'symbol', label: 'Symbol chart', style: 'a loom diagram for black-and-white printing, each band a black line in its own style, solid, dashed or dotted, the pegs open circles', palette: 'black ink only', framing: 'the diagram filling most of a white sheet with a narrow clean margin'},
        {key: 'graph', label: 'Graph paper', style: 'a hand-drawn loom diagram, bands coloured in with felt-tip pens, slightly uneven strokes, pegs ringed in pencil, on blue squared graph paper', palette: 'bright felt-tip colours on pale blue grid paper', framing: 'the diagram drawn on a plain sheet with a small margin all round'},
    ],
    directions: ['a heart', 'summer colors', 'a charm'],
    directionHint: 'try: a heart',
    seo: {
        title: 'Loom band patterns: fishtail, starburst, ladder',
        metaDescription: 'Nine loom band diagrams: fishtail, starburst and ladder bracelets, with numbered bands on the pegs, in three styles.',
        h1: 'Loom band patterns: fishtail, starburst and ladder bracelets',
        tagline: 'Pegs in rows, each band a numbered line between two pegs, in the order to place them.',
        intro: 'A loom band pattern shows where each rubber band goes and in what colour, so a child or an adult can place them in order on a pegged loom. These nine cover a fishtail, a starburst and a rainbow ladder bracelet. Each band is a numbered line between two pegs. Choose a colour diagram, a symbol diagram, or a hand-drawn sheet.',
        useCases: [
            "a bracelet for a child's party",
            'a classroom loom activity',
            'a rainbow bracelet',
            'a charm bracelet',
        ],
        faq: [
            {q: 'How do I read the numbers?', a: 'Each band is numbered in the order you place it, from the small black number at its start.'},
            {q: 'What do the line styles mean in the symbol diagram?', a: 'Each band has its own line style, solid, dashed or dotted, so the bands stay apart in black and white.'},
            {q: 'Can I change the colours?', a: 'Yes. Type the colours you want. The samples here keep theirs.'},
        ],
    },
}
